import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, MenuItem, TableCell, TableRow, TextField, Typography } from '@mui/material';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { DataTable, useAppSnackbar } from '../../common/TableDesign';
import { InventoryItemPicker, getErrorMessage, unwrap } from './InventoryShared';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { Download as DownloadIcon } from '@mui/icons-material';

const REPORTS = [
  { value: 'valuation', label: 'Stock Valuation' },
  { value: 'movement', label: 'Movement (period)' },
  { value: 'grni', label: 'GRNI Exposure' },
  { value: 'margin', label: 'Sales Margin' },
];

// Inventory reports (verified against InventoryReportsController):
// - valuation: live qty/unit-cost/value per item, optional category filter.
// - movement: per-item received/issued/adjusted aggregates with opening and
//   closing balances; REQUIRES from/to dates.
// - grni-exposure: posted receipts with no AP invoice linked (no filters).
// - sales-margin: revenue vs COGS for AR-linked sale issues; same date
//   filters as movement.
const InventoryReportsTab = () => {
  const { can } = useAccessControl();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const [reportType, setReportType] = useState('valuation');
  const [filters, setFilters] = useState({
    category: '',
    item_id: '',
    from_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!can(RESOURCES.LEDGER.INVENTORY, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Inventory reports.</Alert>;
  }

  const runReport = async () => {
    if ((reportType === 'movement' || reportType === 'margin') && (!filters.from_date || !filters.to_date)) {
      showSnackbar('From and to dates are required', 'error');
      return;
    }
    try {
      setLoading(true);
      let res;
      if (reportType === 'valuation') {
        res = await ledgerAPI.getInvValuation(filters.category?.trim() ? { category: filters.category.trim() } : {});
      } else if (reportType === 'movement') {
        const params = { from_date: filters.from_date, to_date: filters.to_date };
        if (filters.item_id) params.item_id = filters.item_id;
        res = await ledgerAPI.getInvMovement(params);
      } else if (reportType === 'margin') {
        const params = { from_date: filters.from_date, to_date: filters.to_date };
        if (filters.item_id) params.item_id = filters.item_id;
        res = await ledgerAPI.getInvSalesMargin(params);
      } else {
        res = await ledgerAPI.getInvGrniExposure();
      }
      setReport(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch report'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (reportType === 'valuation') {
      downloadCsv(
        'inventory-valuation.csv',
        ['Code', 'Item', 'Category', 'UoM', 'Method', 'On Hand', 'Unit Cost', 'Stock Value'],
        valuationRows().map((r) => [r.item_code, r.item_name, r.category || '', r.uom || '', r.costing_method || '', r.quantity_on_hand ?? '', r.unit_cost ?? '', r.stock_value ?? ''])
      );
    } else if (reportType === 'movement') {
      downloadCsv(
        'inventory-movement.csv',
        ['Code', 'Item', 'Received Qty', 'Received Value', 'Issued Qty', 'Issued Value', 'Adjusted Qty', 'Adjusted Value', 'Closing Qty', 'Closing Value'],
        movementRows().map((r) => [r.item_code, r.item_name, r.received_qty ?? '', r.received_value ?? '', r.issued_qty ?? '', r.issued_value ?? '', r.adjusted_qty ?? '', r.adjusted_value ?? '', r.closing_qty ?? '', r.closing_value ?? ''])
      );
    }
  };

  const valuationRows = () => (Array.isArray(report?.rows) ? report.rows : []);
  const movementRows = () => (Array.isArray(report?.rows) ? report.rows : []);

  const renderValuation = () => {
    const rows = valuationRows();
    return (
      <>
        <DataTable
          columns={['Code', 'Item', 'On Hand', 'Unit Cost', 'Stock Value']}
          data={rows}
          loading={false}
          emptyMessage="No stock rows found."
          renderRow={(r, i) => (
            <TableRow key={r.item_id ?? i}>
              <TableCell><strong>{r.item_code}</strong></TableCell>
              <TableCell>{r.item_name}</TableCell>
              <TableCell align="right">{r.quantity_on_hand ?? '-'}</TableCell>
              <TableCell align="right">{formatAccountingAmount(r.unit_cost)}</TableCell>
              <TableCell align="right">{formatAccountingAmount(r.stock_value)}</TableCell>
            </TableRow>
          )}
        />
        <Typography variant="body2" align="right" sx={{ mt: 1 }}>
          {report?.totals?.items ?? rows.length} items - total value: <strong>{formatAccountingAmount(report?.totals?.stock_value)}</strong>
        </Typography>
      </>
    );
  };

  const renderMovement = () => {
    const rows = movementRows();
    return (
      <>
        <DataTable
          columns={['Item', 'Received', 'Issued', 'Adjusted', 'Closing']}
          data={rows}
          loading={false}
          emptyMessage="No posted movements in this period."
          renderRow={(r, i) => (
            <TableRow key={r.item_id ?? i}>
              <TableCell><strong>{r.item_code}</strong> {r.item_name}</TableCell>
              <TableCell align="right">{r.received_qty ?? 0} ({formatAccountingAmount(r.received_value)})</TableCell>
              <TableCell align="right">{r.issued_qty ?? 0} ({formatAccountingAmount(r.issued_value)})</TableCell>
              <TableCell align="right">{r.adjusted_qty ?? 0} ({formatAccountingAmount(r.adjusted_value)})</TableCell>
              <TableCell align="right">{r.closing_qty ?? '-'} ({formatAccountingAmount(r.closing_value)})</TableCell>
            </TableRow>
          )}
        />
        {report?.totals && (
          <Typography variant="body2" align="right" sx={{ mt: 1 }}>
            Received: <strong>{formatAccountingAmount(report.totals.received_value)}</strong>
            {' '}- Issued: <strong>{formatAccountingAmount(report.totals.issued_value)}</strong>
            {' '}- Adjusted: <strong>{formatAccountingAmount(report.totals.adjusted_value)}</strong>
          </Typography>
        )}
      </>
    );
  };

  const renderGrni = () => {
    const exposed = Array.isArray(report?.exposed) ? report.exposed : [];
    return (
      <>
        <DataTable
          columns={['Receipt #', 'Date', 'Supplier', 'Unlinked Value']}
          data={exposed}
          loading={false}
          emptyMessage="No unlinked receipts - every posted receipt has its AP invoice."
          renderRow={(r, i) => (
            <TableRow key={r.receipt_id ?? i}>
              <TableCell><strong>{r.receipt_number}</strong></TableCell>
              <TableCell>{r.receipt_date ? new Date(r.receipt_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</TableCell>
              <TableCell>{r.supplier_id ? `Supplier ${r.supplier_id}` : '-'}</TableCell>
              <TableCell align="right">{formatAccountingAmount(r.total_amount)}</TableCell>
            </TableRow>
          )}
        />
        {report?.totals && (
          <Typography variant="body2" align="right" sx={{ mt: 1 }}>
            {report.totals.unlinked ?? 0} of {report.totals.open_receipts ?? 0} receipts unlinked - exposure: <strong>{formatAccountingAmount(report.totals.exposed_value)}</strong>
          </Typography>
        )}
      </>
    );
  };

  const renderMargin = () => {
    const rows = Array.isArray(report?.rows) ? report.rows : (Array.isArray(report) ? report : []);
    return (
      <DataTable
        columns={['Item', 'Revenue', 'COGS', 'Margin']}
        data={rows}
        loading={false}
        emptyMessage="No AR-linked sale issues in this period."
        renderRow={(r, i) => (
          <TableRow key={r.item_id ?? i}>
            <TableCell><strong>{r.item_code || ''}</strong> {r.item_name || ''}</TableCell>
            <TableCell align="right">{formatAccountingAmount(r.revenue ?? r.total_revenue)}</TableCell>
            <TableCell align="right">{formatAccountingAmount(r.cogs ?? r.total_cogs)}</TableCell>
            <TableCell align="right">{formatAccountingAmount(r.margin ?? (r.revenue != null && r.cogs != null ? Number(r.revenue) - Number(r.cogs) : null))}</TableCell>
          </TableRow>
        )}
      />
    );
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="flex-start">
            <TextField size="small" select label="Report" value={reportType} onChange={(e) => { setReportType(e.target.value); setReport(null); }} sx={{ width: 220 }}>
              {REPORTS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            {reportType === 'valuation' && (
              <TextField
                size="small" label="Category (optional)" value={filters.category}
                onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))} sx={{ width: 200 }}
              />
            )}
            {(reportType === 'movement' || reportType === 'margin') && (
              <>
                <TextField
                  size="small" type="date" label="From" value={filters.from_date}
                  onChange={(e) => setFilters((p) => ({ ...p, from_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }} sx={{ width: 160 }} required
                />
                <TextField
                  size="small" type="date" label="To" value={filters.to_date}
                  onChange={(e) => setFilters((p) => ({ ...p, to_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }} sx={{ width: 160 }} required
                />
                <Box sx={{ width: 260 }}>
                  <InventoryItemPicker label="Item (optional)" value={filters.item_id} onChange={(id) => setFilters((p) => ({ ...p, item_id: id }))} />
                </Box>
              </>
            )}
          </Box>
          <Box display="flex" gap={1} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={runReport} disabled={loading}>
              {loading ? 'Loading...' : 'Run Report'}
            </Button>
            {(reportType === 'valuation' || reportType === 'movement') && (
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!report}>
                Export CSV
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : !report ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              Choose a report and run it.
            </Typography>
          ) : reportType === 'valuation' ? renderValuation()
            : reportType === 'movement' ? renderMovement()
              : reportType === 'grni' ? renderGrni()
                : renderMargin()}
        </CardContent>
      </Card>

      {SnackbarComponent}
    </Box>
  );
};

export default InventoryReportsTab;
